// src/assets/components/SelectGroup.jsx
import React from "react";
import chevron from "../icons/Arrow/chevron.svg"
import downTri from "../icons/Arrow/down_tri.svg"
import upTri from "../icons/Arrow/up_tri.svg"

const cx = (...c) => c.filter(Boolean).join(" ");

/**
 * 공통 카드 베이스
 * - label: 상단 타이틀
 * - subLabel: 하단 서브타이틀 (옵션)
 * - value: 선택된 값 (없으면 placeholder 표시)
 * - placeholder: 우측 기본 문구 (기본 "입력해주세요")
 * - onClick: 우측 영역 클릭 시 동작
 */
function SelectCardBase({
  label,               // 혹시 label로 넘기는 경우도 지원
  value,
  placeholder = "입력해주세요",
  subLabel,
  subPlaceholder,
  onClick,
}) {
  const title = label || "타이틀";
  const hasValue = Boolean(value && String(value).trim());
  const rightText = hasValue ? value : placeholder;
  const rightColorClass = hasValue
    ? "text-text-subtler"
    : "text-text-primary";

  const trimmedSub = (subLabel ?? "").trim();
  const hasSubValue = !!trimmedSub;
  const showSub = hasSubValue || !!subPlaceholder;

  const subText = hasSubValue
    ? trimmedSub
    : subPlaceholder || "";

  const subColorClass = hasSubValue
    ? "text-text-subtle"
    : "text-text-disabled";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-[16px] pr-[12px] rounded-[16px] bg-surface-light-subtle"
    >
      {/* 1줄: Left Label + Right Select */}
      <div className="flex items-center">
        <div className="flex-1 text-t4 text-text-subtle">
          {title}
        </div>

        <div className="ml-4 flex items-center gap-1">
          <span className={cx("text-b1", rightColorClass)}>
            {rightText}
          </span>
          <img src={chevron} alt="" className="w-[24px] h-[24px]" />
        </div>
      </div>

      {/* 2줄: 서브타이틀 */}
      {showSub && (
        <div className={cx("text-c2 pt-[8px]", subColorClass)}>
          {subText}
        </div>
      )}
    </button>
  );
}

/** 1) 서브타이틀 없는 버전 */
export function SelectCard(props) {
  return (
    <SelectCardBase
      {...props}
      subLabel={undefined}
      subPlaceholder={undefined}
    />
  );
}


/** 2) 서브타이틀 있는 버전 */
export function SelectCardWithSub({
  subLabel = "",
  subPlaceholder = "작성한 내용이 없습니다.",
  ...rest
}) {
  return (
    <SelectCardBase
      {...rest}
      subLabel={subLabel}
      subPlaceholder={subPlaceholder}
    />
  );
}

export function SelectBoxB({
  value,
  placeholder = "텍스트를 입력해주세요",
  title,
  titleClassName = "",

  status = "default",   // "default" | "error"
  errorText = "",       // 에러 문구

  isOpen = false,
  useToggleIcon = false,
  rightIcon,

  onClick,
  disabled = false,
  className = "",
  children,
}) {
  const hasTitle = title != null && String(title).trim() !== "";
  const hasValue = value != null && String(value).trim() !== "";

  const isTitleMode = hasTitle;
  const isError = status === "error";

  const mainTitle = isTitleMode
    ? title
    : hasValue
    ? value
    : placeholder;

  // 아이콘 결정
  let iconNode = null;
  if (rightIcon !== undefined) {
    iconNode = rightIcon;
  } else if (useToggleIcon) {
    iconNode = (
      <img
        src={isOpen ? upTri : downTri}
        alt=""
        className="w-[24px] h-[24px]"
      />
    );
  } else {
    iconNode = (
      <img src={downTri} alt="" className="w-[24px] h-[24px]" />
    );
  }

  // 🔸 기본 타이포 + 색
  const defaultTextClass =
    "text-b1 " +
    (isError
      ? "text-text-danger"
      : isTitleMode
      ? "text-text-subtle"
      : hasValue
      ? "text-text-subtle"
      : "text-text-disabled");

  // 🔸 titleClassName이 있으면 통째로 오버라이드
  const textClass = titleClassName || defaultTextClass;

  const handleClick = (e) => {
    if (disabled) return;
    if (typeof onClick === "function") onClick(e);
  };

  return (
    <div className={`w-full ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={
          "w-full min-h-[48px] text-left p-[12px] pl-[16px] rounded-[12px] bg-surface-light-white border-border-default border-[1px] flex items-center justify-between " +
          (disabled ? "opacity-40 cursor-not-allowed " : "")
        }
      >
        <span className={textClass}>
          {mainTitle}
        </span>
        {iconNode}
      </button>

       {isError && errorText && (
        <p className="mt-[4px] text-c2 text-status-error">
          {errorText}
        </p>
      )}

      {isOpen && !!children && (
        <div
          className="
            mt-[8px]
            rounded-[12px]
            border-border-default border-[1px]
            bg-surface-light-white
          "
        >
          {children}
        </div>
      )}
    </div>
  );
}
